import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { X, Save, Shield } from 'lucide-react'
import api from '../services/api'

const RoleModal = ({ isOpen, onClose, role = null, onSuccess }) => {
    const [loading, setLoading] = useState(false)
    const [permissions, setPermissions] = useState({
        users: { create: false, read: false, update: false, delete: false },
        roles: { create: false, read: false, update: false, delete: false },
        cameras: { create: false, read: false, update: false, delete: false, playback: false },
        auth: { login: true, signup: true }
    })

    const {
        register,
        handleSubmit,
        reset,
        setValue,
        formState: { errors }
    } = useForm({
        defaultValues: {
            name: '',
            description: '',
            level: 1
        }
    })

    useEffect(() => {
        if (role) {
            // Populate form fields for editing
            setValue('name', role.name || '')
            setValue('description', role.description || '')
            setValue('level', role.level || 1)

            // Set permissions
            setPermissions(role.permissions || {
                users: { create: false, read: false, update: false, delete: false },
                roles: { create: false, read: false, update: false, delete: false },
                cameras: { create: false, read: false, update: false, delete: false, playback: false },
                auth: { login: true, signup: true }
            })
        } else {
            // Reset form for new role
            setValue('name', '')
            setValue('description', '')
            setValue('level', 1)

            setPermissions({
                users: { create: false, read: false, update: false, delete: false },
                roles: { create: false, read: false, update: false, delete: false },
                cameras: { create: false, read: false, update: false, delete: false, playback: false },
                auth: { login: true, signup: true }
            })
        }
    }, [role, setValue])

    // Reset form when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            if (role) {
                // Populate form for editing
                setValue('name', role.name || '')
                setValue('description', role.description || '')
                setValue('level', role.level || 1)
            } else {
                // Reset form for new role
                reset()
            }
        }
    }, [isOpen, role, setValue, reset])

    const handlePermissionChange = (resource, action, value) => {
        setPermissions(prev => ({
            ...prev,
            [resource]: {
                ...prev[resource],
                [action]: value
            }
        }))
    }

    const onSubmit = async (data) => {
        setLoading(true)
        try {
            const roleData = {
                ...data,
                permissions,
                level: parseInt(data.level)
            }

            if (role) {
                // Update existing role
                await api.put(`/roles/${role._id}`, roleData)
            } else {
                // Create new role
                await api.post('/roles', roleData)
            }

            onSuccess()
            onClose()
            reset()
            setPermissions({
                users: { create: false, read: false, update: false, delete: false },
                roles: { create: false, read: false, update: false, delete: false },
                cameras: { create: false, read: false, update: false, delete: false, playback: false },
                auth: { login: true, signup: true }
            })
        } catch (error) {
            console.error('Error saving role:', error)
        } finally {
            setLoading(false)
        }
    }

    if (!isOpen) return null

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                    <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={onClose}></div>
                </div>

                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center">
                                <Shield className="h-6 w-6 text-primary-600 mr-2" />
                                <h3 className="text-lg font-medium text-gray-900">
                                    {role ? `Edit Role: ${role.name}` : 'Add New Role'}
                                </h3>
                            </div>
                            <button
                                onClick={onClose}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    Role Name
                                </label>
                                <input
                                    type="text"
                                    className={`input mt-1 ${role?.name?.toLowerCase() === 'super admin' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                    placeholder="Enter role name"
                                    disabled={role?.name?.toLowerCase() === 'super admin'}
                                    {...register('name', {
                                        required: 'Role name is required',
                                        minLength: {
                                            value: 2,
                                            message: 'Role name must be at least 2 characters'
                                        },
                                        maxLength: {
                                            value: 50,
                                            message: 'Role name cannot exceed 50 characters'
                                        }
                                    })}
                                />
                                {role?.name?.toLowerCase() === 'super admin' && (
                                    <p className="mt-1 text-sm text-gray-500">Super admin role name cannot be changed</p>
                                )}
                                {errors.name && (
                                    <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    Description
                                </label>
                                <textarea
                                    className="input mt-1"
                                    rows="3"
                                    placeholder="Enter role description"
                                    {...register('description', {
                                        maxLength: {
                                            value: 200,
                                            message: 'Description cannot exceed 200 characters'
                                        }
                                    })}
                                />
                                {errors.description && (
                                    <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    Level
                                </label>
                                <select
                                    className="input mt-1"
                                    {...register('level', {
                                        required: 'Level is required',
                                        min: { value: 1, message: 'Level must be at least 1' },
                                        max: { value: 10, message: 'Level cannot exceed 10' }
                                    })}
                                >
                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(level => (
                                        <option key={level} value={level}>
                                            Level {level}
                                        </option>
                                    ))}
                                </select>
                                {errors.level && (
                                    <p className="mt-1 text-sm text-red-600">{errors.level.message}</p>
                                )}
                            </div>

                            {/* Permissions Section */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-3">
                                    Permissions
                                </label>

                                {/* Users Permissions */}
                                <div className="space-y-3">
                                    <div>
                                        <h4 className="text-sm font-medium text-gray-700 mb-2">User Management</h4>
                                        <div className="grid grid-cols-2 gap-2">
                                            {['read', 'create', 'update', 'delete'].map(action => (
                                                <label key={action} className="flex items-center space-x-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={permissions.users[action]}
                                                        onChange={(e) => handlePermissionChange('users', action, e.target.checked)}
                                                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                                    />
                                                    <span className="text-sm text-gray-700 capitalize">{action}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Roles Permissions */}
                                    <div>
                                        <h4 className="text-sm font-medium text-gray-700 mb-2">Role Management</h4>
                                        <div className="grid grid-cols-2 gap-2">
                                            {['read', 'create', 'update', 'delete'].map(action => (
                                                <label key={action} className="flex items-center space-x-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={permissions.roles[action]}
                                                        onChange={(e) => handlePermissionChange('roles', action, e.target.checked)}
                                                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                                    />
                                                    <span className="text-sm text-gray-700 capitalize">{action}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Cameras Permissions */}
                                    <div>
                                        <h4 className="text-sm font-medium text-gray-700 mb-2">Camera Management</h4>
                                        <p className="text-xs text-gray-500 mb-2">Control access to camera operations and playback functionality</p>
                                        <div className="grid grid-cols-2 gap-2">
                                            {['read', 'create', 'update', 'delete', 'playback'].map(action => (
                                                <label key={action} className="flex items-center space-x-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={permissions.cameras?.[action] || false}
                                                        onChange={(e) => handlePermissionChange('cameras', action, e.target.checked)}
                                                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                                    />
                                                    <span className="text-sm text-gray-700 capitalize">{action}</span>
                                                </label>
                                            ))}
                                        </div>
                                        <p className="text-xs text-gray-500 mt-2">
                                            <strong>Playback:</strong> Allows users to access recorded footage, playback controls, and RTSP URL generation
                                        </p>
                                    </div>

                                    {/* Auth Permissions */}
                                    <div>
                                        <h4 className="text-sm font-medium text-gray-700 mb-2">Authentication</h4>
                                        <div className="grid grid-cols-2 gap-2">
                                            {['login', 'signup'].map(action => (
                                                <label key={action} className="flex items-center space-x-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={permissions.auth[action]}
                                                        onChange={(e) => handlePermissionChange('auth', action, e.target.checked)}
                                                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                                    />
                                                    <span className="text-sm text-gray-700 capitalize">{action}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>

                    <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                        <button
                            type="submit"
                            onClick={handleSubmit(onSubmit)}
                            disabled={loading}
                            className="btn btn-primary w-full sm:w-auto sm:ml-3"
                        >
                            {loading ? (
                                <div className="flex items-center">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    {role ? 'Updating...' : 'Creating...'}
                                </div>
                            ) : (
                                <div className="flex items-center">
                                    <Save className="h-4 w-4 mr-2" />
                                    {role ? 'Update Role' : 'Create Role'}
                                </div>
                            )}
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="btn btn-outline w-full sm:w-auto mt-3 sm:mt-0"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default RoleModal 